import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import AccommodationRoomPackageGroup from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationRoomPackageGroup'
import AccommodationPackageRoomOverview from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageRoomOverview'
import AccommodationPackageSelectionOptions from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageSelectionOptions'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomDetailsListContent from 'components/AccommodationKit/AccommodationRoomDetailsListContent'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import { getAccommodationRoomTileActionsState } from 'components/AccommodationKit/getAccommodationRoomTileActionsState'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import PackageSelectionRoomThumbnail from 'components/OfferPage/packageSelection/PackageSelectionRoomThumbnail'
import PackageSelectionUnavailablePackages, { isUnavailablePackageSelectionState } from 'components/OfferPage/packageSelection/PackageSelectionUnavailablePackages'
import PackageSelectionUnavailableBanner from 'components/OfferPage/packageSelection/PackageSelectionUnavailableBanner'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import ModalContext from 'contexts/ModalContext'
import { type OfferPageRoom, type FlashSameRoomSelection, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { isRenderablePackageView } from '../SameRoom/FlashHotelSameRoomOption'
import FlashHotelRoomDetailsModal from '../FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import FlashPackageCard from './FlashPackageCard'
import AccommodationPackageSelectionSkeleton from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageSelectionSkeleton'
import useRoomPackageInclusionsDisclosure from 'components/AccommodationKit/AccommodationPackageSelection/useRoomPackageInclusionsDisclosure'
import type { FlashPackageSelection, FlashPackageViewsByOccupancy } from './flashPackageSelection'
import type { PackageSelectionBurnQuote } from 'components/OfferPage/packageSelection/packageSelectionBurnQuote'

const RoomImage = styled(AccommodationRoomTileImage)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 0;
  > * { min-height: 0; }
`

const RoomImageFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
`

const ImageUrgency = styled.div`
  position: absolute;
  z-index: 1;
  inset-block-start: ${rem(16)};
  inset-inline-start: ${rem(16)};
`

const MobileUrgency = styled.div`
  margin-block-end: ${rem(8)};
  ${mediaQueryUp.tablet} { display: none; }
`

interface RoomProps {
  offer: App.HotelOffer
  checkIn?: string
  isLoading: boolean
  availableForPurchase: boolean
  views: Array<App.OfferPackageOptionView>
  availableRoomCount?: number
  singleRoomSelection?: FlashPackageSelection
  singleRoomBurnQuote?: PackageSelectionBurnQuote
  burnUnavailable?: boolean
  viewsByOccupancy: FlashPackageViewsByOccupancy
  rooms: Array<OfferPageRoom>
  sameRoomSelection?: FlashSameRoomSelection
  selectedRoomTypeId?: string
  requiresRates: boolean
  suggestedPackage?: { option: App.OfferPackageOptionView, message: string }
}

function FlashPackageRoom({ singleRoomSelection, singleRoomBurnQuote, burnUnavailable, offer, views, availableRoomCount, viewsByOccupancy, rooms, sameRoomSelection, selectedRoomTypeId, requiresRates, suggestedPackage, checkIn, isLoading, availableForPurchase }: RoomProps) {
  const inclusionsDisclosure = useRoomPackageInclusionsDisclosure()
  const showModal = useContext(ModalContext)
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const first = views[0]
  const pkg = first?.package
  const roomType = pkg?.roomType
  const showDetails = useCallback(() => {
    if (roomType) showModal(<FlashHotelRoomDetailsModal room={roomType} />)
  }, [roomType, showModal])
  const onChangeDates = useCallback(() => {
    dispatch({ type: OfferPageActions.setRoomType, room: roomType })
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [dispatch, roomType])
  const onChangeOccupancy = useCallback(() => {
    dispatch({ type: OfferPageActions.setRoomType, room: roomType })
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [dispatch, roomType])
  const datesAvailable = views.some(view => view.hotelAvailable)
  const soldOutLabel = intl.formatMessage({
    id: 'r939r00GL7ijU93v_IDp',
    defaultMessage: 'SOLD OUT',
    description: 'Label indicating sold out status on the offer detail page',
  })
  const urgencyLabel = <>
    {(!isLoading && !datesAvailable) && (<UrgencyLabel variant="critical2">{soldOutLabel}</UrgencyLabel>)}
    {!(!isLoading && !datesAvailable) && (availableRoomCount ? <RoomsRemainingLabel roomCount={availableRoomCount} threshold="rate" /> : undefined)}
  </>
  const actionsState = isLoading ? 'loading' : getAccommodationRoomTileActionsState({
    availableForPurchase,
    roomFitsCapacity: views.some(view => view.capacityValid),
    datesAvailable,
    checkIn,
  })
  if (!pkg || !roomType || !isHotelPackage(pkg)) return null

  const overview = <AccommodationPackageRoomOverview
    thumbnail={roomType.images[0]?.id ? <PackageSelectionRoomThumbnail room={roomType} width={125} height={84} /> : undefined}
    gallery={<RoomImageFrame>
      <RoomImage roomPackage={pkg} showBookmark={false} />
      {urgencyLabel && <ImageUrgency>{urgencyLabel}</ImageUrgency>}
    </RoomImageFrame>}
    title={<Heading as="h3" variant="heading3">{roomType.name}</Heading>}
  >
    <AccommodationRoomDetailsListContent
      roomPackage={pkg}
      presentation="compact"
      showHotelRoomAmenities
      maxCompactAmenities={7}
      showStandaloneAmenitiesAndPoliciesCta
      urgencyLabels={urgencyLabel ? <MobileUrgency>{urgencyLabel}</MobileUrgency> : undefined}
      amenitiesAndPoliciesCta={<TextLink onClick={showDetails} size="caption" wrap="no-wrap" endIcon={<LineAngleRightBIcon />}>
        {intl.formatMessage({
          id: 'W4j1E8zEw-4KG33yUXg8',
          defaultMessage: 'More details',
          description: 'Link to see room details from the Flash hotel package selection overview',
        })}
      </TextLink>}
    />
  </AccommodationPackageRoomOverview>

  const renderableViews = views.filter(view => isRenderablePackageView(view, requiresRates))
  const showUnavailablePackages = !isLoading && !renderableViews.length && isUnavailablePackageSelectionState(actionsState)

  return <AccommodationRoomPackageGroup
    overview={overview}
    loading={isLoading}
    banner={showUnavailablePackages ? <PackageSelectionUnavailableBanner onChangeDates={onChangeDates} onChangeOccupancy={onChangeOccupancy} /> : undefined}
  >
    {showUnavailablePackages && <PackageSelectionUnavailablePackages onChangeDates={onChangeDates} onChangeOccupancy={onChangeOccupancy} />}
    {!isLoading && !renderableViews.length && !showUnavailablePackages && <AccommodationRoomTileActions
      roomPackage={pkg}
      state={actionsState ?? (checkIn ? 'sold-out-on-dates' : 'available-on-dates')}
      shouldShowAmenitiesAndPoliciesCta={false}
      onChangeDatesClick={onChangeDates}
      onChangeOccupancyClick={onChangeOccupancy}
      onCheckAvailabilityClick={onChangeDates}
    />}
    <>
      {(isLoading && !renderableViews.length) && (<AccommodationPackageSelectionSkeleton packageCount={views.length} />)}
      {!showUnavailablePackages && !(isLoading && !renderableViews.length) && (<AccommodationPackageSelectionOptions id={inclusionsDisclosure.contentId}>
        {renderableViews.map(view => <FlashPackageCard
          offer={offer}
          checkIn={checkIn}
          key={view.package.uniqueKey}
          view={view}
          showAllInclusions={!isLoading && offer.packages.length > 0 && renderableViews.length === 1}
          inclusionsDisclosure={inclusionsDisclosure}
          singleRoomSelection={singleRoomSelection}
          singleRoomBurnQuote={singleRoomBurnQuote}
          burnUnavailable={burnUnavailable}
          viewsByOccupancy={viewsByOccupancy}
          rooms={rooms}
          sameRoomSelection={sameRoomSelection}
          selectedRoomTypeId={selectedRoomTypeId}
          loading={isLoading}
          requiresRates={requiresRates}
          checkInventory={!!checkIn}
          suggestedPackageMessage={suggestedPackage?.option.package.uniqueKey === view.package.uniqueKey ? suggestedPackage.message : undefined}
        />)}
      </AccommodationPackageSelectionOptions>)}
    </>
  </AccommodationRoomPackageGroup>
}

export default FlashPackageRoom
